import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SwitchField, TextareaField, TextField } from '@/components/form-fields';
import { ReadOnlyNotice, SectionCard, SettingsSkeleton } from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  getSiteSettings,
  type SiteSettingsRecord,
  SOCIAL_KEYS,
  saveSiteSettings,
} from '@/lib/settings/storefront';
import { useForm } from '@/lib/use-form';

/**
 * The storefront's copy and switches: the age gate, the announcement bar,
 * what search engines see, and where the social links point.
 *
 * ctrl-saas wrote these only during onboarding, so a store that changed its
 * mind about the age-gate wording had no way to say so.
 */

const schema = z.object({
  ageGateEnabled: z.boolean(),
  ageGateCopy: z.string().trim().max(500),
  announcement: z.string().trim().max(200),
  seoTitle: z.string().trim().max(120),
  seoDescription: z.string().trim().max(300),
  instagram: z.string().trim().max(200),
  facebook: z.string().trim().max(200),
  x: z.string().trim().max(200),
  tiktok: z.string().trim().max(200),
  youtube: z.string().trim().max(200),
  requireAccount: z.boolean(),
});

type Values = z.infer<typeof schema>;

function toValues(settings: SiteSettingsRecord): Values {
  return {
    ageGateEnabled: settings.ageGateEnabled,
    ageGateCopy: settings.ageGateCopy,
    announcement: settings.announcement,
    seoTitle: settings.seoTitle,
    seoDescription: settings.seoDescription,
    instagram: settings.social.instagram ?? '',
    facebook: settings.social.facebook ?? '',
    x: settings.social.x ?? '',
    tiktok: settings.social.tiktok ?? '',
    youtube: settings.social.youtube ?? '',
    requireAccount: settings.requireAccount,
  };
}

export function StorefrontSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['site-settings', accountId],
    queryFn: () => getSiteSettings(accountId),
  });

  const { values, errors, isDirty, set, reset, submit } = useForm<Values>(
    toValues(settings.data ?? ({} as SiteSettingsRecord)),
    schema,
  );

  useEffect(() => {
    if (settings.data) reset(toValues(settings.data));
  }, [settings.data, reset]);

  const save = useMutation({
    mutationFn: (parsed: Values) =>
      saveSiteSettings(accountId, {
        age_gate_enabled: parsed.ageGateEnabled,
        age_gate_copy: parsed.ageGateCopy || null,
        announcement: parsed.announcement || null,
        seo: { title: parsed.seoTitle, description: parsed.seoDescription },
        social: Object.fromEntries(
          SOCIAL_KEYS.map((key) => [key, parsed[key]]).filter(([, value]) => value),
        ),
        checkout_settings: {
          cartBehavior: settings.data?.cartBehavior ?? 'drawer',
          requireAccount: parsed.requireAccount,
        },
      }),
    onSuccess: () => {
      toast.success('Storefront settings saved.');
      queryClient.invalidateQueries({ queryKey: ['site-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (settings.isLoading) return <SettingsSkeleton />;

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex max-w-3xl flex-col gap-6"
    >
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="Age gate"
          description="The 21+ interstitial every visitor sees once. Confirming sets a cookie the server reads, so it does not flash on the next page."
        >
          <SwitchField
            id="age-gate-enabled"
            label="Show the age gate"
            checked={values.ageGateEnabled}
            onChange={(checked) => set('ageGateEnabled', checked)}
          />

          <TextareaField
            id="age-gate-copy"
            label="Wording"
            description="Left blank, the storefront uses a neutral 21+ line."
            rows={2}
            value={values.ageGateCopy}
            error={errors.ageGateCopy}
            onChange={(value) => set('ageGateCopy', value)}
          />
        </SectionCard>

        <SectionCard
          title="Announcement"
          description="A single line above the header. Empty hides the bar entirely."
        >
          <TextField
            id="announcement"
            label="Message"
            placeholder="Now delivering across the East Bay — order by 8pm"
            value={values.announcement}
            error={errors.announcement}
            onChange={(value) => set('announcement', value)}
          />
        </SectionCard>

        <SectionCard
          title="Checkout"
          description="Whether shoppers must have an account to order. Guests are matched to an existing customer by email either way."
        >
          <SwitchField
            id="require-account"
            label="Require an account at checkout"
            checked={values.requireAccount}
            onChange={(checked) => set('requireAccount', checked)}
          />
        </SectionCard>

        <SectionCard
          title="Search and social"
          description="What a search result and a shared link show, and where the footer icons point."
        >
          <TextField
            id="seo-title"
            label="Site title"
            value={values.seoTitle}
            error={errors.seoTitle}
            onChange={(value) => set('seoTitle', value)}
          />

          <TextareaField
            id="seo-description"
            label="Description"
            rows={2}
            value={values.seoDescription}
            error={errors.seoDescription}
            onChange={(value) => set('seoDescription', value)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            {SOCIAL_KEYS.map((key) => (
              <TextField
                key={key}
                id={`social-${key}`}
                label={key === 'x' ? 'X' : key[0]?.toUpperCase() + key.slice(1)}
                placeholder="https://…"
                value={values[key]}
                error={errors[key]}
                onChange={(value) => set(key, value)}
              />
            ))}
          </div>
        </SectionCard>

        <div className="flex gap-2">
          <Button type="submit" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
        </div>
      </fieldset>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Chat                                                                        */
/* -------------------------------------------------------------------------- */

const chatSchema = z.object({
  enabled: z.boolean(),
  agentName: z.string().trim().max(60),
  greeting: z.string().trim().max(300),
  awayMessage: z.string().trim().max(300),
  askForEmail: z.boolean(),
});

type ChatValues = z.infer<typeof chatSchema>;

export function ChatSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['site-settings', accountId],
    queryFn: () => getSiteSettings(accountId),
  });

  const initial: ChatValues = {
    enabled: settings.data?.chatEnabled ?? false,
    agentName: settings.data?.chat.agentName ?? '',
    greeting: settings.data?.chat.greeting ?? '',
    awayMessage: settings.data?.chat.awayMessage ?? '',
    askForEmail: settings.data?.chat.askForEmail ?? true,
  };

  const { values, errors, isDirty, set, reset, submit } = useForm(initial, chatSchema);

  useEffect(() => {
    if (settings.data) {
      reset({
        enabled: settings.data.chatEnabled,
        agentName: settings.data.chat.agentName,
        greeting: settings.data.chat.greeting,
        awayMessage: settings.data.chat.awayMessage,
        askForEmail: settings.data.chat.askForEmail,
      });
    }
  }, [settings.data, reset]);

  const save = useMutation({
    mutationFn: (parsed: ChatValues) =>
      saveSiteSettings(accountId, {
        chat_enabled: parsed.enabled,
        chat_settings: {
          agentName: parsed.agentName,
          greeting: parsed.greeting,
          awayMessage: parsed.awayMessage,
          askForEmail: parsed.askForEmail,
        },
      }),
    onSuccess: () => {
      toast.success('Chat settings saved.');
      queryClient.invalidateQueries({ queryKey: ['site-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (settings.isLoading) return <SettingsSkeleton />;

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex max-w-3xl flex-col gap-6"
    >
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="Live chat"
          description="A launcher on the storefront. Threads land in Messages, so somebody has to be watching it."
        >
          <SwitchField
            id="chat-enabled"
            label="Show the chat widget"
            checked={values.enabled}
            onChange={(checked) => set('enabled', checked)}
          />

          <TextField
            id="chat-agent"
            label="Who the shopper is talking to"
            placeholder="The shop"
            value={values.agentName}
            error={errors.agentName}
            onChange={(value) => set('agentName', value)}
          />

          <TextareaField
            id="chat-greeting"
            label="Greeting"
            rows={2}
            value={values.greeting}
            error={errors.greeting}
            onChange={(value) => set('greeting', value)}
          />

          <TextareaField
            id="chat-away"
            label="Away message"
            description="Shown outside opening hours."
            rows={2}
            value={values.awayMessage}
            error={errors.awayMessage}
            onChange={(value) => set('awayMessage', value)}
          />

          <SwitchField
            id="chat-email"
            label="Ask for an email before the first message"
            description="So a reply can reach them after they close the tab."
            checked={values.askForEmail}
            onChange={(checked) => set('askForEmail', checked)}
          />
        </SectionCard>

        <div className="flex gap-2">
          <Button type="submit" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
        </div>
      </fieldset>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Blog                                                                        */
/* -------------------------------------------------------------------------- */

const blogSchema = z.object({
  enabled: z.boolean(),
  title: z.string().trim().max(60),
  description: z.string().trim().max(300),
});

type BlogValues = z.infer<typeof blogSchema>;

export function BlogSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('cms.manage');
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['site-settings', accountId],
    queryFn: () => getSiteSettings(accountId),
  });

  const { values, errors, isDirty, set, reset, submit } = useForm<BlogValues>(
    {
      enabled: settings.data?.blogEnabled ?? false,
      title: settings.data?.blog.title ?? 'Journal',
      description: settings.data?.blog.description ?? '',
    },
    blogSchema,
  );

  useEffect(() => {
    if (settings.data) {
      reset({
        enabled: settings.data.blogEnabled,
        title: settings.data.blog.title,
        description: settings.data.blog.description,
      });
    }
  }, [settings.data, reset]);

  const save = useMutation({
    mutationFn: (parsed: BlogValues) =>
      saveSiteSettings(accountId, {
        blog_enabled: parsed.enabled,
        blog_settings: { title: parsed.title, description: parsed.description },
      }),
    onSuccess: () => {
      toast.success('Blog settings saved.');
      queryClient.invalidateQueries({ queryKey: ['site-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (settings.isLoading) return <SettingsSkeleton />;

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex max-w-3xl flex-col gap-6"
    >
      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="Blog"
          description="Off, the storefront's /blog answers 404 and nothing links to it. Posts are written under Content."
        >
          <SwitchField
            id="blog-enabled"
            label="Show the blog on the storefront"
            checked={values.enabled}
            onChange={(checked) => set('enabled', checked)}
          />

          <TextField
            id="blog-title"
            label="What readers see it called"
            placeholder="Journal"
            value={values.title}
            error={errors.title}
            onChange={(value) => set('title', value)}
          />

          <TextareaField
            id="blog-description"
            label="Description"
            rows={2}
            value={values.description}
            error={errors.description}
            onChange={(value) => set('description', value)}
          />
        </SectionCard>

        <div className="flex gap-2">
          <Button type="submit" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
        </div>
      </fieldset>
    </form>
  );
}
